import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import { Button } from '../ui/Button';

// "Something changed" — the only card in the app that interrupts.
//
// It appears when several signals in one physiological system moved together
// over a couple of weeks, which is roughly monthly at most (the detector is
// deliberately hard to trigger). Everything about this component follows from
// two decisions made in the design:
//
//   • **We do not know the cause, so we ask.** The ranked items are labelled as
//     things that *also* changed, never as explanations. The user holds the
//     variables no wearable can see — illness, travel, a brutal fortnight at
//     work — so the card's real job is to be easy to answer, which is why the
//     guesses are one-tap chips with a free-text escape hatch rather than an
//     open question most people would scroll past.
//
//   • **It must not be missable.** The card reports itself as seen only once it
//     has actually rendered, and until the user answers or waves it off it keeps
//     its place at the top of the home page — for weeks if that's how long it
//     takes them to come back. A push goes out too (see notify_anomaly), but the
//     card is the thing that waits.
//
// Unlike its neighbours this card does NOT hide itself when it has nothing to
// say — a quiet fortnight is good news and worth stating plainly, or the
// absence of an alert is indistinguishable from a broken feature.

const AnomalyCard = ({ onResolved }) => {
  const [anomaly, setAnomaly] = useState(null);
  const [loading, setLoading] = useState(true);
  // "We couldn't ask" and "we asked and all is well" must not look the same:
  // the all-clear below is a positive claim about the user's health, and a
  // failed request is not entitled to make it.
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/anomalies/current');
      setAnomaly(data?.anomaly || null);
      setFailed(false);
    } catch {
      setAnomaly(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Acknowledge only once it's on screen — that's what "seen" is supposed to
  // mean. Fire-and-forget: failing to record the view must never block reading.
  useEffect(() => {
    if (anomaly?.id && anomaly.status === 'unseen') {
      apiClient.post(`/api/anomalies/${anomaly.id}/seen`).catch(() => {});
    }
  }, [anomaly?.id, anomaly?.status]);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/anomalies/${anomaly.id}/answer`, {
        choice,
        text: text.trim() || null,
      });
      toast.success('Thanks — noted.');
      setAnomaly(null);
      onResolved?.();
    } catch {
      toast.error('Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/api/anomalies/${anomaly.id}/dismiss`);
      setAnomaly(null);
      onResolved?.();
    } catch {
      toast.error('Could not dismiss that.');
    } finally {
      setBusy(false);
    }
  };

  // Silence beats a wrong reassurance: if the check didn't complete, say
  // nothing rather than implying everything is fine.
  if (loading || failed) return null;

  // The quiet state. Deliberately present rather than hidden: this card is the
  // app's promise to tell you when something moves, and saying "nothing did" is
  // how that promise stays visible on the ~29 days a month it's keeping quiet.
  if (!anomaly) {
    return (
      <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
          Nothing unusual
        </h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Your recovery, sleep and mood signals are all running in their normal
          range. We'll say so here if that changes.
        </p>
      </div>
    );
  }

  const better = anomaly.direction === 'better';
  const ranked = (anomaly.guesses || []).filter((g) => g.kind !== 'prompt');
  const prompts = (anomaly.guesses || []).filter((g) => g.kind === 'prompt');

  return (
    // Green edge for good news, gold for "look at this" — the reserved
    // three-colour system, carried by the border so the card body keeps the same
    // surface as every other card on the page.
    <div
      className={`card-glow rounded-2xl border-2 bg-surface-card dark:bg-surface-dark-card p-5 transition-shadow hover:shadow-card ${
        better ? 'border-good-strong dark:border-good' : 'border-primary-500'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="inline-block rounded-full bg-primary-500 px-2.5 py-0.5 text-sm font-semibold text-[#1a1200]">
            {better ? 'Something improved' : 'Something changed'}
          </span>
          <h2 className="mt-2 text-lg font-bold text-gray-900 dark:text-white">
            {anomaly.headline}
          </h2>
        </div>
      </div>

      {/* The evidence. Shown because a claim this loud has to show its work —
          and because seeing which numbers moved is often what jogs the memory
          of what was going on that fortnight. */}
      <ul className="mt-3 space-y-1.5">
        {(anomaly.metrics || []).map((m) => (
          <li key={m.key} className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-gray-800 dark:text-gray-200">
              {m.label}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {m.baseline}
              {' → '}
              {m.recent} {m.unit}
            </span>
          </li>
        ))}
      </ul>

      {/* Ranked coincidences. The wording never claims causation, because with
          one person and one event we genuinely cannot know. */}
      {ranked.length > 0 && (
        <div className="mt-4 rounded-lg border border-surface-border dark:border-surface-dark-border p-3">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            What else changed around the same time
          </p>
          <ul className="mt-1.5 space-y-1">
            {ranked.map((g) => (
              <li key={g.key} className="text-sm text-gray-700 dark:text-gray-300">
                {g.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The ask. One tap is the whole design: an open question gets scrolled
          past, a chip gets answered. */}
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Do you know what was going on? It helps us learn what moves your
          numbers.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {[...ranked, ...prompts].map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setChoice(g.key)}
              aria-pressed={choice === g.key}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                choice === g.key
                  ? 'border-primary-500 bg-primary-500 text-[#1a1200]'
                  : 'border-surface-border text-gray-800 hover:bg-gray-50 dark:border-surface-dark-border dark:text-gray-200 dark:hover:bg-surface-dark-raised'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        {choice && (
          <textarea
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Anything else worth noting? (optional)"
            className="mt-3 w-full rounded-md border border-gray-300 dark:border-surface-dark-border bg-white dark:bg-surface-dark p-2 text-base text-gray-900 dark:text-gray-100"
          />
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button onClick={submit} disabled={busy || !choice}>
            Save
          </Button>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            className="text-sm text-gray-700 dark:text-gray-300 underline"
          >
            Not now
          </button>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        This is a change in your own measurements, not a diagnosis. If something
        feels wrong or this keeps up, talk to a clinician.
      </p>
    </div>
  );
};

export default AnomalyCard;
